import { useCallback, useMemo, useState } from 'react'
import { Table } from '@mantine/core'
import { useIsMutating } from '@tanstack/react-query'

import { GoalStatus } from '@/types/actionPlan.types'

import { ClaimGoalModal } from '@/modules/actionPlan/pages/components/ClaimGoalModal/ClaimGoalModal'
import { EditGoal } from '@/modules/actionPlan/pages/components/EditGoal/EditGoal'
import { GoalModal } from '@/modules/actionPlan/pages/components/GoalModal/GoalModal'
import { TableItem } from '@/modules/actionPlan/pages/components/TableItem/TableItem'
import { useGoalItemDelete } from '@/modules/actionPlan/pages/components/TableList/TableList.config'
import type { TableListProps } from '@/modules/actionPlan/pages/components/TableList/TableList.types'

import { ConfirmationModal } from '@/components/ConfirmationModal/ConfirmationModal'

import {
  useArchiveGoal,
  useCompleteGoal,
  useReactivateGoal,
} from '@/hooks/useGoalMutations.hook'

export function TableList({ items, readOnly, status }: TableListProps) {
  const [openedDelete, setOpenedDelete] = useState<boolean>(false)
  const [openedEdit, setOpenedEdit] = useState<boolean>(false)
  const [openedClaim, setOpenedClaim] = useState<boolean>(false)
  const [openedComplete, setOpenedComplete] = useState<boolean>(false)
  const [openedArchive, setOpenedArchive] = useState<boolean>(false)
  const [openedReactivate, setOpenedReactivate] = useState<boolean>(false)
  const [currentGoalId, setCurrentGoalId] = useState<string>('')

  const isMutating = useIsMutating({
    exact: true,
  })

  const findGoal = useCallback(
    (id: string | undefined) => items.find(goal => goal.id.toString() === id),
    [items],
  )

  const currentGoal = useMemo(
    () => findGoal(currentGoalId),
    [currentGoalId, findGoal],
  )

  const { deleteGoal } = useGoalItemDelete(currentGoalId)

  const completeGoalMutation = useCompleteGoal(() => setOpenedComplete(false))
  const archiveGoalMutation = useArchiveGoal(() => setOpenedArchive(false))
  const reactivateGoalMutation = useReactivateGoal(() =>
    setOpenedReactivate(false),
  )

  const handleOpenModal =
    (setter: (v: boolean) => void) => (id: string | undefined) => {
      if (!id) return
      setCurrentGoalId(id)
      setter(true)
    }

  const handleOpenDeleteModal = handleOpenModal(setOpenedDelete)
  const handleOpenEditModal = handleOpenModal(setOpenedEdit)
  const handleOpenClaimModal = handleOpenModal(setOpenedClaim)
  const handleOpenCompleteModal = handleOpenModal(setOpenedComplete)
  const handleOpenArchiveModal = handleOpenModal(setOpenedArchive)
  const handleOpenReactivateModal = handleOpenModal(setOpenedReactivate)

  const handleDeleteGoal = () => {
    if (!currentGoalId) return
    deleteGoal()
  }

  const handleCompleteGoal = () => {
    if (!currentGoalId) return
    completeGoalMutation.mutate({ goalId: +currentGoalId })
  }

  const handleArchiveGoal = () => {
    if (!currentGoalId) return
    archiveGoalMutation.mutate({ goalId: +currentGoalId })
  }

  const handleReactivateGoal = () => {
    if (!currentGoalId) return
    reactivateGoalMutation.mutate({ goalId: +currentGoalId })
  }

  const isActive = status === GoalStatus.ACTIVE

  return (
    <>
      <Table.Tbody>
        {items.length === 0 && <TableItem empty />}

        {items.map(item => {
          const archiveModal =
            !readOnly && status !== GoalStatus.ARCHIVED
              ? handleOpenArchiveModal
              : undefined
          const reactivateModal =
            !readOnly && status !== GoalStatus.ACTIVE
              ? handleOpenReactivateModal
              : undefined

          return (
            <TableItem
              key={item.id}
              item={item}
              openArchiveModal={archiveModal}
              openClaimModal={
                !readOnly && isActive ? handleOpenClaimModal : undefined
              }
              openCompleteModal={
                !readOnly && isActive ? handleOpenCompleteModal : undefined
              }
              openDeleteModal={
                !readOnly && isActive ? handleOpenDeleteModal : undefined
              }
              openEditModal={
                !readOnly && isActive ? handleOpenEditModal : undefined
              }
              openReactivateModal={reactivateModal}
              readOnly={readOnly}
            />
          )
        })}
      </Table.Tbody>

      {!readOnly && (
        <>
          <ConfirmationModal
            button={{ label: 'Delete', variant: 'error' }}
            description={`Are you sure you want to delete "${currentGoal?.name}"?`}
            isLoading={!!isMutating}
            onClose={() => setOpenedDelete(false)}
            onConfirm={handleDeleteGoal}
            opened={openedDelete}
            title="Delete goal"
          />

          <ConfirmationModal
            button={{ label: 'Complete', variant: 'primary' }}
            description={`Are you sure you want to mark "${currentGoal?.name}" as completed?`}
            isLoading={completeGoalMutation.isPending}
            onClose={() => setOpenedComplete(false)}
            onConfirm={handleCompleteGoal}
            opened={openedComplete}
            title="Complete goal"
          />

          <ConfirmationModal
            button={{ label: 'Archive', variant: 'error' }}
            description={`Are you sure you want to archive "${currentGoal?.name}"?`}
            isLoading={archiveGoalMutation.isPending}
            onClose={() => setOpenedArchive(false)}
            onConfirm={handleArchiveGoal}
            opened={openedArchive}
            title="Archive goal"
          />

          <ConfirmationModal
            button={{ label: 'Reactivate', variant: 'primary' }}
            description={`Are you sure you want to reactivate "${currentGoal?.name}"?`}
            isLoading={reactivateGoalMutation.isPending}
            onClose={() => setOpenedReactivate(false)}
            onConfirm={handleReactivateGoal}
            opened={openedReactivate}
            title="Reactivate goal"
          />

          <GoalModal onClose={() => setOpenedEdit(false)} opened={openedEdit}>
            <EditGoal goal={currentGoal} onClose={() => setOpenedEdit(false)} />
          </GoalModal>

          {currentGoal && (
            <ClaimGoalModal
              goal={currentGoal}
              onClose={() => setOpenedClaim(false)}
              opened={openedClaim}
            />
          )}
        </>
      )}
    </>
  )
}
